// src/pages/Blog.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Calendar, 
  User, 
  Tag, 
  Clock, 
  ChevronRight, 
  MessageCircle, 
  Share2,
  BookOpen,
  Globe,
  Coffee,
  TrendingUp
} from 'lucide-react';
import '../styles/blog.css';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');


  
  // Categories for blog posts
  const categories = [
    { id: 'all', name: 'All Posts', icon: <BookOpen size={18} /> },
    { id: 'restaurant-spotlights', name: 'Restaurant Spotlights', icon: <Coffee size={18} /> },
    { id: 'halal-guides', name: 'Halal Food Guides', icon: <Tag size={18} /> },
    { id: 'travel', name: 'Travel & Destinations', icon: <Globe size={18} /> },
    { id: 'industry-news', name: 'Industry News', icon: <TrendingUp size={18} /> }
  ];
  
  useEffect(() => {
    // Simulate API fetch
    setIsLoading(true);
    setTimeout(() => {
      const allPosts = getBlogPosts();
      setPosts(allPosts.regular);
      setFeaturedPosts(allPosts.featured);
      setIsLoading(false);
    }, 800);
  }, []);
  
  // Filter posts based on category and search query
  const filteredPosts = posts.filter(post => {
    let matchesCategory = activeCategory === 'all' || post.category === activeCategory;
    let matchesSearch = true;
    
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      matchesSearch = post.title.toLowerCase().includes(query) || 
                      post.excerpt.toLowerCase().includes(query);
    }
    
    return matchesCategory && matchesSearch;
  });
  
  // Sample blog post data
  const getBlogPosts = () => {
    return {
      featured: [
        {
          id: 1,
          title: "The Rise of Halal Dining in Major US Cities",
          excerpt: "Exploring the growing popularity of halal restaurants across American metropolitan areas and what's driving this culinary trend.",
          category: "industry-news",
          author: "Sarah Johnson",
          date: "February 15, 2025",
          readTime: 6,
          image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2074",
          comments: 24,
          featured: true
        },
        {
          id: 2,
          title: "10 Must-Visit Halal Restaurants in New York City",
          excerpt: "From hidden gems to famous establishments, discover the best halal dining experiences the Big Apple has to offer.",
          category: "halal-guides",
          author: "Mohammed Ali",
          date: "February 10, 2025",
          readTime: 8,
          image: "https://images.unsplash.com/photo-1518684079-3c830dcef090?q=80&w=2070",
          comments: 36,
          featured: true
        },
        {
          id: 3,
          title: "How Al-Madina Grill Transformed with Technology",
          excerpt: "A success story of how one family-owned restaurant embraced digital tools to grow their business and customer base.",
          category: "restaurant-spotlights",
          author: "Fatima Khan",
          date: "February 5, 2025",
          readTime: 5,
          image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070",
          comments: 19,
          featured: true
        }
      ],
      regular: [
        {
          id: 4,
          title: "Understanding Halal Certification: A Restaurant Owner's Guide",
          excerpt: "Everything restaurant owners need to know about the process, benefits, and marketing advantages of halal certification.",
          category: "industry-news",
          author: "Ahmed Hassan",
          date: "February 1, 2025",
          readTime: 7,
          image: "https://images.unsplash.com/photo-1577219491135-ce391730fb2c?q=80&w=2070",
          comments: 15
        },
        {
          id: 5,
          title: "The Perfect Halal Food Tour of London",
          excerpt: "A neighborhood-by-neighborhood guide to London's vibrant and diverse halal food scene.",
          category: "travel",
          author: "Zainab Mir",
          date: "January 25, 2025",
          readTime: 9,
          image: "https://images.unsplash.com/photo-1546412414-e1885e51cfa9?q=80&w=2148",
          comments: 28
        },
        {
          id: 6,
          title: "From Food Truck to Restaurant Empire: The Story of Kabab Express",
          excerpt: "How a single food truck specializing in halal street food grew into a multi-location restaurant business.",
          category: "restaurant-spotlights",
          author: "Omar Qureshi",
          date: "January 20, 2025",
          readTime: 6,
          image: "https://images.unsplash.com/photo-1590846406792-0adc7f938f1d?q=80&w=2069",
          comments: 22
        },
        {
          id: 7,
          title: "Digital Marketing Strategies for Halal Restaurants",
          excerpt: "Effective digital marketing tactics specifically tailored for restaurants serving halal cuisine.",
          category: "industry-news",
          author: "Layla Ahmad",
          date: "January 15, 2025",
          readTime: 5,
          image: "https://images.unsplash.com/photo-1516321497487-e288fb19713f?q=80&w=2070",
          comments: 17
        },
        {
          id: 8,
          title: "The Best Halal Brunch Spots in Chicago",
          excerpt: "Where to find the most delicious halal brunch offerings in the Windy City.",
          category: "halal-guides",
          author: "Yusuf Patel",
          date: "January 10, 2025",
          readTime: 4,
          image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2070",
          comments: 31
        },
        {
          id: 9,
          title: "Halal Dining in Paris: A Traveler's Guide",
          excerpt: "Navigating Paris's growing halal food scene - from traditional French cuisine with halal options to authentic Middle Eastern restaurants.",
          category: "travel",
          author: "Sophia Martin",
          date: "January 5, 2025",
          readTime: 8,
          image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?q=80&w=2073",
          comments: 26
        },
        {
          id: 10,
          title: "How Saffron House Became a Community Hub",
          excerpt: "The story of how one restaurant became the center of cultural and community events in their neighborhood.",
          category: "restaurant-spotlights",
          author: "Hasan Ali",
          date: "December 30, 2024",
          readTime: 6,
          image: "https://images.unsplash.com/photo-1552566626-52f8b828add9?q=80&w=2070",
          comments: 13
        },
        {
          id: 11,
          title: "Fusion Halal: Where Tradition Meets Innovation",
          excerpt: "Exploring the trend of halal fusion cuisine and the chefs pioneering these culinary crossovers.",
          category: "halal-guides",
          author: "Nadia Khan",
          date: "December 25, 2024",
          readTime: 7,
          image: "https://images.unsplash.com/photo-1600891964599-f61f4298bdbc?q=80&w=2070",
          comments: 19
        },
        {
          id: 12,
          title: "Tech Tools Every Modern Restaurant Needs",
          excerpt: "Essential technology solutions that can help streamline operations and improve customer experience in halal restaurants.",
          category: "industry-news",
          author: "Tariq Mahmood",
          date: "December 20, 2024",
          readTime: 5,
          image: "https://images.unsplash.com/photo-1606857521015-7f9fcf423740?q=80&w=2070",
          comments: 11
        },
        {
          id: 13,
          title: "Dubai's Hidden Halal Gems: Beyond the Tourist Spots",
          excerpt: "Discover authentic local halal eateries away from Dubai's glitzy tourist attractions.",
          category: "travel",
          author: "Amina Hussein",
          date: "December 15, 2024",
          readTime: 6,
          image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=2070",
          comments: 24
        }
      ]
    };
  };
  
  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : '';
  };
  
  const getCategoryIcon = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.icon : <Tag size={18} />;
  };
  
  if (isLoading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading blog posts...</p>
      </div>
    );
  }
  
  return (
    <div className="blog-page">
      {/* Blog Header */}
      <div className="blog-header">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h1 className="display-4 fw-bold">Our Blog</h1>
              <p className="lead">Insights, guides, and stories from the halal dining world</p>
            </div>
            <div className="col-lg-6">
              <div className="search-container">
                <div className="input-group">
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Search blog posts..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <button className="btn btn-primary">
                    <Search size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Featured Posts Carousel */}
      {featuredPosts.length > 0 && (
        <div className="featured-posts-section">
          <div className="container">
            <div className="featured-posts-carousel">
              {featuredPosts.map(post => (
                <div key={post.id} className="featured-post-card">
                  <div className="featured-post-image">
                    <img src={post.image} alt={post.title} />
                    <div className="featured-badge">Featured</div>
                  </div>
                  <div className="featured-post-content">
                    <div className="post-category">
                      {getCategoryIcon(post.category)}
                      <span>{getCategoryName(post.category)}</span>
                    </div>
                    <h2 className="post-title">{post.title}</h2>
                    <p className="post-excerpt">{post.excerpt}</p>
                    <div className="post-meta">
                      <div className="post-author">
                        <User size={16} />
                        <span>{post.author}</span>
                      </div>
                      <div className="post-date">
                        <Calendar size={16} />
                        <span>{post.date}</span>
                      </div>
                      <div className="post-read-time">
                        <Clock size={16} />
                        <span>{post.readTime} min read</span>
                      </div>
                    </div>
                    <Link to={`/blog/${post.id}`} className="btn btn-primary read-more-btn">
                      Read Article <ChevronRight size={16} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Blog Posts Section */}
      <div className="blog-posts-section">
        <div className="container">
          <div className="row">
            <div className="col-lg-3">
              {/* Categories */}
              <div className="blog-sidebar">
                <div className="categories-card">
                  <h3>Categories</h3>
                  <ul className="categories-list">
                    {categories.map(category => (
                      <li 
                        key={category.id} 
                        className={activeCategory === category.id ? 'active' : ''}
                        onClick={() => setActiveCategory(category.id)}
                      >
                        <span className="category-icon" style={{ background: "none" }}>{category.icon}</span>
                        <span className="category-name">{category.name}</span>
                        <span className="post-count">
                          {category.id === 'all' 
                            ? posts.length 
                            : posts.filter(post => post.category === category.id).length}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Popular Tags */}
                <div className="tags-card">
                  <h3>Popular Tags</h3>
                  <div className="tags-container">
                    <span className="tag">Halal Food</span>
                    <span className="tag">Restaurant Tips</span>
                    <span className="tag">New York</span>
                    <span className="tag">London</span>
                    <span className="tag">Marketing</span>
                    <span className="tag">Middle Eastern</span>
                    <span className="tag">Travel</span>
                    <span className="tag">Certification</span>
                    <span className="tag">Success Stories</span>
                    <span className="tag">Technology</span>
                  </div>
                </div>
                
                {/* Newsletter Signup */}
                <div className="newsletter-card">
                  <h3>Subscribe</h3>
                  <p>Get the latest articles and restaurant tips right to your inbox</p>
                  <div className="input-group">
                    <input type="email" className="form-control" placeholder="Your email address" />
                    <button className="btn btn-primary">Subscribe</button>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-lg-9">
              {searchQuery && (
                <div className="search-results-info mb-4">
                  <h2>
                    {filteredPosts.length === 0 
                      ? `No results found for "${searchQuery}"` 
                      : `Search results for "${searchQuery}"`}
                  </h2>
                  {filteredPosts.length === 0 && (
                    <button 
                      className="btn btn-link p-0" 
                      onClick={() => setSearchQuery('')}
                    >
                      Clear search
                    </button>
                  )}
                </div>
              )}
              
              {activeCategory !== 'all' && !searchQuery && (
                <div className="category-header mb-4">
                  <h2>{getCategoryName(activeCategory)}</h2>
                  <p>{filteredPosts.length} articles in this category</p>
                </div>
              )}
              
              {filteredPosts.length === 0 && !searchQuery ? (
                <div className="no-posts">
                  <p>No posts found in this category.</p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => setActiveCategory('all')}
                  >
                    View All Posts
                  </button>
                </div>
              ) : filteredPosts.length === 0 && searchQuery ? (
                <div className="no-posts">
                  <p>No posts match your search criteria.</p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => setSearchQuery('')}
                  >
                    Clear Search
                  </button>
                </div>
              ) : (
                <div className="row g-4">
                  {filteredPosts.map(post => (
                    <div key={post.id} className="col-md-6 col-lg-4">
                      <div className="blog-post-card">
                        <div className="post-image">
                          <img src={post.image} alt={post.title} />
                          <div className="post-category-badge">
                            {getCategoryIcon(post.category)}
                            <span>{getCategoryName(post.category)}</span>
                          </div>
                        </div>
                        <div className="post-content">
                          <h3 className="post-title">
                            <Link to={`/blog/${post.id}`}>{post.title}</Link>
                          </h3>
                          <p className="post-excerpt">{post.excerpt}</p>
                          <div className="post-meta">
                            <div className="post-date">
                              <Calendar size={14} />
                              <span>{post.date}</span>
                            </div>
                            <div className="post-time">
                              <Clock size={14} />
                              <span>{post.readTime} min read</span>
                            </div>
                          </div>
                          <div className="post-footer">
                            <div className="post-author">
                              <div className="author-avatar">
                                {post.author.charAt(0)}
                              </div>
                              <span>{post.author}</span>
                            </div>
                            <div className="post-actions">
                              <button className="action-btn">
                                <MessageCircle size={14} />
                                <span>{post.comments}</span>
                              </button>
                              <button className="action-btn">
                                <Share2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Pagination */}
              {/* {filteredPosts.length > 0 && (
                <div className="pagination-container mt-5">
                <nav aria-label="Blog pagination">
                  <ul className="pagination">
                    <li className="page-item disabled">
                      <span className="page-link">Previous</span>
                    </li>
                    <li className="page-item active">
                      <span className="page-link">1</span>
                    </li>
                    <li className="page-item">
                      <a className="page-link" href="#">2</a>
                    </li>
                    <li className="page-item">
                      <a className="page-link" href="#">3</a>
                    </li>
                    <li className="page-item">
                      <a className="page-link" href="#">Next</a>
                    </li>
                  </ul>
                </nav>
              </div>
              
              )} */}
            </div>
          </div>
        </div>
      </div>
      
      {/* Call to Action */}
      <div className="blog-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Are You a Restaurant Owner?</h2>
            <p>Join our platform to reach thousands of diners looking for authentic halal dining experiences</p>
            <Link to="/register-restaurant" className="btn btn-light btn-lg">
              Register Your Restaurant
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;
